import Link from "next/link";
import Logo from "@/components/brand/Logo";
import { Mail, Linkedin, Twitter, Github } from "lucide-react";

interface FooterLink {
  label: string;
  href: string;
  isReal: boolean;
}

const productLinks: FooterLink[] = [
  { label: "Jobs", href: "/jobs", isReal: true },
  { label: "Knowledge Hub", href: "/knowledge-hub", isReal: true },
  { label: "Insights", href: "/blog", isReal: true },
  // NOTE: /candidates requires an authenticated employer session.
  // It is a real route, but not a public page; update this link if a public
  // candidate directory is added later.
  { label: "Candidates", href: "/candidates", isReal: true },
  // NOTE: Career Intelligence is authenticated (currently /seafarer/career).
  // Point to a public landing page when one is built.
  { label: "Career Intelligence", href: "#", isReal: false },
];

const companyLinks: FooterLink[] = [
  { label: "About", href: "/about", isReal: true },
  { label: "Contact", href: "/contact", isReal: true },
  { label: "FAQ", href: "/faq", isReal: true },
];

const trustSafetyLinks: FooterLink[] = [
  { label: "Trust & Safety", href: "/trust-safety", isReal: true },
  { label: "Recruitment Fraud Awareness", href: "/recruitment-fraud-awareness", isReal: true },
  { label: "Community Guidelines", href: "/community-guidelines", isReal: true },
  { label: "Grievance Redressal", href: "/grievance-redressal", isReal: true },
];

const legalLinks: FooterLink[] = [
  { label: "Privacy", href: "/privacy", isReal: true },
  { label: "Terms", href: "/terms", isReal: true },
  { label: "Cookies", href: "/cookies", isReal: true },
  { label: "Disclaimer", href: "/disclaimer", isReal: true },
  { label: "CV & Profile Consent", href: "/cv-profile-consent", isReal: true },
];

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-panel">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo variant="full" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-ash">
              Verified maritime hiring network connecting seafarers, shore
              professionals, and employers worldwide.
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:col-span-8">
            <FooterGroup title="Product" links={productLinks} />
            <FooterGroup title="Company" links={companyLinks} />
            <FooterGroup title="Trust & Safety" links={trustSafetyLinks} />
            <FooterGroup title="Legal" links={legalLinks} />
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 sm:flex-row">
          <p className="text-xs text-ash">
            &copy; {year} CrewAhead. All rights reserved.
          </p>

          <div className="flex items-center gap-4">
            <SocialLink
              href="mailto:support@crewahead.com"
              label="Email"
              icon={Mail}
            />
            <SocialLink
              href="https://linkedin.com/company/crewahead"
              label="LinkedIn"
              icon={Linkedin}
            />
            <SocialLink
              href="https://twitter.com/crewahead"
              label="Twitter"
              icon={Twitter}
            />
            <SocialLink
              href="https://github.com/crewahead"
              label="GitHub"
              icon={Github}
            />
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterGroup({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
        {title}
      </h3>
      <ul className="mt-4 space-y-3">
        {links.filter((link) => link.isReal).map((link) => (
          <li key={link.label}>
            <Link
              href={link.href}
              className="text-sm text-ash transition-colors hover:text-neon-cyan max-md:inline-flex max-md:min-h-11 max-md:min-w-11 max-md:items-center"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialLink({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: typeof Mail;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center p-3 text-ash transition-colors hover:text-neon-cyan md:p-0"
    >
      <Icon className="h-5 w-5" />
    </a>
  );
}
